// src/components/reports/ReportsList.js - Reports list for journalists
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { getErrorMessage } from '../../utils/error';
import { format } from 'date-fns';
import ReportDetailContent from './ReportDetailContent';
import ReportForm from './ReportForm';

const PAGE_SIZE = 20;

const VIOLATION_TYPE_KEYS = [
  'targeting_civilians',
  'forced_displacement',
  'property_destruction',
  'deprivation_housing_aid',
  'arbitrary_arrest',
  'hate_speech_incitement',
  'other'
];

const ReportsList = ({ onAddToInvestigation, refreshKey }) => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsPage, setReportsPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    violation_type: '',
    is_anonymous: '',
  });
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportDetail, setShowReportDetail] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [reportForEdit, setReportForEdit] = useState(null);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    const id = setTimeout(() => {
      const next = searchInput.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) {
          setReportsPage(0);
        }
        return next;
      });
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  const loadReports = useCallback(async () => {
    if (isLoadingRef.current) {
      return;
    }
    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.violation_type) params.append('violation_type', filters.violation_type);
      if (filters.is_anonymous !== '') params.append('is_anonymous', filters.is_anonymous);
      if (debouncedSearch) params.append('q', debouncedSearch);
      params.append('limit', String(PAGE_SIZE));
      params.append('offset', String(reportsPage * PAGE_SIZE));
      params.append('sort', sortOrder === 'oldest' ? 'asc' : 'desc');

      const response = await api.get(`/api/reports/reports?${params.toString()}`);

      if (response.data && Array.isArray(response.data.reports)) {
        setReports(response.data.reports);
        setReportsTotal(
          typeof response.data.total === 'number'
            ? response.data.total
            : response.data.count ?? 0
        );
      } else if (response.data?.reports && typeof response.data.reports === 'object') {
        const reportsArray = Object.values(response.data.reports);
        setReports(reportsArray);
        setReportsTotal(
          typeof response.data.total === 'number'
            ? response.data.total
            : response.data.count ?? reportsArray.length
        );
      } else {
        setReports([]);
        setReportsTotal(0);
      }
    } catch (err) {
      console.error('[ReportsList] Failed to load reports:', err);
      if (err.response?.status === 403) {
        setError('Access denied - check if user has Team Chayyek role');
      } else {
        setError(getErrorMessage(err, 'Failed to fetch reports'));
      }
      setReports([]);
      setReportsTotal(0);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [filters, debouncedSearch, reportsPage, sortOrder]);

  useEffect(() => {
    loadReports();
  }, [loadReports, refreshKey]);

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowReportDetail(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400',
      verified: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400',
      rejected: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400',
      flagged: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400',
      pending_review: 'bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light',
    };
    return colors[status] || colors.pending;
  };

  const getCategoryLabel = (category) => {
    const raw = String(category || '').trim();
    if (!raw) return 'N/A';
    return t(`report.categories.${raw}`, { defaultValue: raw });
  };

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 dark:bg-shayek-dark-50 rounded-lg p-4 border border-shayek-light-300 dark:border-shayek-dark-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="md:col-span-2 lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              {t('journalist.reports.filters.search')}
            </label>
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('journalist.reports.filters.searchPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg text-sm bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              {t('journalist.reports.filters.status')}
            </label>
            <select
              value={filters.status}
              onChange={(e) => {
                setReportsPage(0);
                setFilters({ ...filters, status: e.target.value });
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg text-sm bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900"
            >
              <option value="">{t('journalist.reports.filters.allStatuses')}</option>
              <option value="pending">{t('journalist.reports.status.pending')}</option>
              <option value="verified">{t('journalist.reports.status.verified')}</option>
              <option value="rejected">{t('journalist.reports.status.rejected')}</option>
              <option value="flagged">{t('journalist.reports.status.flagged')}</option>
              <option value="pending_review">{t('journalist.reports.status.pending_review')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              {t('journalist.reports.filters.violationType')}
            </label>
            <select
              value={filters.violation_type}
              onChange={(e) => {
                setReportsPage(0);
                setFilters({ ...filters, violation_type: e.target.value });
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg text-sm bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900"
            >
              <option value="">{t('journalist.reports.filters.allViolationTypes')}</option>
              {VIOLATION_TYPE_KEYS.map((key) => (
                <option key={key} value={key}>
                  {t(`report.violationTypeOptions.${key}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              {t('journalist.reports.filters.type')}
            </label>
            <select
              value={filters.is_anonymous}
              onChange={(e) => {
                setReportsPage(0);
                setFilters({ ...filters, is_anonymous: e.target.value });
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg text-sm bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900"
            >
              <option value="">{t('journalist.reports.filters.allTypes')}</option>
              <option value="true">{t('journalist.reports.anonymous')}</option>
              <option value="false">{t('journalist.reports.named')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              {t('journalist.reports.filters.sortBy') || 'Sort by Date'}
            </label>
            <select
              value={sortOrder}
              onChange={(e) => {
                setReportsPage(0);
                setSortOrder(e.target.value);
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg text-sm bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900"
            >
              <option value="newest">{t('journalist.reports.filters.newest') || 'Newest First'}</option>
              <option value="oldest">{t('journalist.reports.filters.oldest') || 'Oldest First'}</option>
            </select>
          </div>
          <div className="md:col-span-2 lg:col-span-6 flex flex-wrap gap-2 items-end">
            <button
              type="button"
              onClick={() => {
                setReportsPage(0);
                setFilters({ status: '', violation_type: '', is_anonymous: '' });
                setSearchInput('');
              }}
              className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-shayek-dark-200 bg-white dark:bg-shayek-dark-100 text-gray-700 dark:text-shayek-dark-300 hover:bg-gray-100 dark:hover:bg-shayek-dark-200"
            >
              {t('journalist.reports.filters.clear')}
            </button>
            <p className="text-sm text-gray-600 dark:text-shayek-dark-600 ml-auto">
              {t('journalist.reports.filters.totalCount', { count: reportsTotal })}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-red-800 dark:text-red-400 text-sm whitespace-pre-line">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shayek-primary dark:border-shayek-red-light mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600 dark:text-shayek-dark-600">{t('common.loading')}</p>
        </div>
      ) : reports.length === 0 && !error ? (
        <div className="text-center py-8 text-gray-500 dark:text-shayek-dark-500">
          <p>{t('journalist.reports.empty')}</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {reports.map((report) => (
              <div
                key={report.id}
                className="bg-white dark:bg-shayek-dark-50 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                        {t(`journalist.reports.status.${report.status}`) || report.status}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-300 rounded-full text-xs font-medium">
                        {getCategoryLabel(report.category)}
                      </span>
                      {report.is_anonymous && (
                        <span className="px-2 py-1 bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light rounded-full text-xs font-medium">
                          {t('journalist.reports.anonymous')}
                        </span>
                      )}
                      <span className="text-xs text-gray-500 dark:text-shayek-dark-500">
                        {format(new Date(report.created_at), 'MMM d, yyyy HH:mm')}
                      </span>
                    </div>
                    {report.title && (
                      <p className="text-sm font-medium text-gray-900 dark:text-shayek-dark-900 mb-1 line-clamp-1">
                        {report.title}
                      </p>
                    )}
                    <p className="text-gray-900 dark:text-shayek-dark-900 mb-2 line-clamp-2 break-words overflow-hidden">
                      {report.input_preview || report.input_data?.substring(0, 200) || t('journalist.reports.noPreview')}
                    </p>
                    {report.location_country && (
                      <p className="text-sm text-gray-600 dark:text-shayek-dark-600">
                        📍 {report.location_country} {report.location_region && `- ${report.location_region}`}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleViewReport(report)}
                      className="px-3 py-1 bg-shayek-primary dark:bg-shayek-red-light text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark transition-colors text-sm"
                    >
                      {t('journalist.reports.view')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setReportForEdit(report);
                        setShowEditModal(true);
                      }}
                      className="px-3 py-1 bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-300 rounded-lg hover:bg-gray-300 dark:hover:bg-shayek-dark-300 transition-colors text-sm"
                    >
                      {t('journalist.reports.edit')}
                    </button>
                    {onAddToInvestigation && (
                      <button
                        type="button"
                        onClick={() => onAddToInvestigation(report)}
                        className="px-3 py-1 bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-300 rounded-lg hover:bg-gray-300 dark:hover:bg-shayek-dark-300 transition-colors text-sm"
                      >
                        {t('journalist.reports.addToInvestigation')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {reportsTotal > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-gray-200 dark:border-shayek-dark-200">
              <p className="text-sm text-gray-600 dark:text-shayek-dark-600">
                {t('journalist.reports.filters.pageOf', {
                  current: reportsPage + 1,
                  total: Math.max(1, Math.ceil(reportsTotal / PAGE_SIZE))
                })}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={reportsPage <= 0}
                  onClick={() => setReportsPage((p) => Math.max(0, p - 1))}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-shayek-dark-200 bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-shayek-dark-200"
                >
                  {t('journalist.reports.filters.prev')}
                </button>
                <button
                  type="button"
                  disabled={(reportsPage + 1) * PAGE_SIZE >= reportsTotal}
                  onClick={() => setReportsPage((p) => p + 1)}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-shayek-dark-200 bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-shayek-dark-200"
                >
                  {t('journalist.reports.filters.next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showReportDetail && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-shayek-light-300 dark:border-shayek-dark-200">
            <div className="p-6 border-b border-gray-200 dark:border-shayek-dark-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-shayek-dark-900">{t('journalist.reports.details.title')}</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowReportDetail(false);
                    setSelectedReport(null);
                  }}
                  className="text-gray-400 dark:text-shayek-dark-400 hover:text-gray-600 dark:hover:text-shayek-dark-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <ReportDetailContent report={selectedReport} showStatusBadge={true} />
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowReportDetail(false);
                    setSelectedReport(null);
                  }}
                  className="px-6 py-2 bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-300 rounded-lg hover:bg-gray-300 dark:hover:bg-shayek-dark-300 transition-colors"
                >
                  {t('common.close')}
                </button>
                {onAddToInvestigation && (
                  <button
                    type="button"
                    onClick={() => {
                      onAddToInvestigation(selectedReport);
                      setShowReportDetail(false);
                      setSelectedReport(null);
                    }}
                    className="px-6 py-2 bg-shayek-primary dark:bg-shayek-red-light text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark transition-colors"
                  >
                    {t('journalist.reports.addToInvestigation')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && reportForEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-shayek-light-300 dark:border-shayek-dark-200">
            <div className="p-6 border-b border-gray-200 dark:border-shayek-dark-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-shayek-dark-900">{t('journalist.reports.edit')}</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setReportForEdit(null);
                  }}
                  className="text-gray-400 dark:text-shayek-dark-400 hover:text-gray-600 dark:hover:text-shayek-dark-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              <ReportForm
                initialData={reportForEdit}
                reportId={reportForEdit?.id}
                editMode={true}
                onSuccess={() => {
                  setShowEditModal(false);
                  setReportForEdit(null);
                  loadReports();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsList;
